'use client';

import React, { useEffect, useState } from 'react';
import { Facet, buildFacet } from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';

type AppliedFacetFiltersProps = {
  fields: string[];
  labels?: Record<string, string>;
};

type SelectedFacet = {
  field: string;
  label: string;
  value: string;
  facet: Facet;
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024); // Tailwind 'lg'
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isDesktop;
};

const AppliedFacetFilters = ({ fields, labels = {} }: AppliedFacetFiltersProps) => {
  const engine = useCoveoEngine();
  const [selectedFacets, setSelectedFacets] = useState<SelectedFacet[]>([]);
  const [facetControllers, setFacetControllers] = useState<Record<string, Facet>>({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const isDesktop = useIsDesktop();
  const { getDictionaryValue } = useDictionary();

  useEffect(() => {
    if (!engine || fields.length === 0) return;

    const newControllers: Record<string, Facet> = {};

    const builtFacets = fields.map((field) => {
      const facet = buildFacet(engine, {
        options: {
          field,
          facetId: field,
          numberOfValues: 50,
        },
      });
      newControllers[field] = facet;

      return {
        facet,
        field,
        label: labels[field] || field,
      };
    });

    setFacetControllers(newControllers);

    const unsubscribeFns = builtFacets.map(({ facet, field, label }) =>
      facet.subscribe(() => {
        const selectedValues = facet.state.values.filter((val) => val.state === 'selected');
        setSelectedFacets((prev) => {
          const others = prev.filter((f) => f.field !== field);
          const newOnes = selectedValues.map((v) => ({
            field,
            label,
            value: v.value,
            facet,
          }));
          return [...others, ...newOnes];
        });
      })
    );

    return () => unsubscribeFns.forEach((fn) => fn());
  }, [engine, fields, labels]);

  const handleClearAll = () => {
    Object.values(facetControllers).forEach((facet) => {
      facet.deselectAll();
    });
  };

  if (selectedFacets.length === 0) return null;

  const filterCount = selectedFacets.length;
  const dictionaryClear = getDictionaryValue('ClearAllFilters') || 'Clear all filters';
  const dictionaryShow = getDictionaryValue('Show') || 'Show {num}';
  const dictionaryLess = getDictionaryValue('ShowLess') || 'Show less';

  return (
    <div className="my-6 flex flex-wrap gap-8 items-center">
      {/* Mobile collapsed button */}
      {!isDesktop && !showMobileFilters && (
        <div>
          <button
            onClick={() => setShowMobileFilters(true)}
            className="text-16 text-blue-600 hover:no-underline font-semibold py-5 px-8 rounded-[30px] border border-gray-100 font-nunitoSans leading-[25px]"
          >
            {dictionaryShow.replace('{num}', filterCount.toString())}
          </button>
        </div>
      )}

      {/* Applied Filters Display */}
      {(isDesktop || showMobileFilters) && (
        <>
          <div className="applied-facet-filters flex flex-wrap gap-8">
            {selectedFacets.map(({ field, label, value, facet }) => (
              <div
                key={`${field}-${value}`}
                className="flex flex-wrap items-center bg-white border border-gray-100 pl-8 pr-6 py-5 text-gray-900 rounded-[30px] text-16 leading-[25px] font-semibold"
              >
                <div className="inline-block w-[calc(100%-24px)] whitespace-nowrap">
                  <span className="mr-2 font-semibold">{label}:</span>
                  <span className="mr-2">{value}</span>
                </div>
                <button
                  onClick={() => {
                    const target = facet.state.values.find((v) => v.value === value);
                    if (target) {
                      facet.toggleSelect(target);
                    }
                  }}
                  className="text-gray-500 hover:text-red-600 text-xs font-bold inline-block"
                  title="Remove"
                  aria-label="Remove filter"
                >
                  <SvgIcon
                    icon={'close'}
                    className="ml-4 w-[22px] h-[22px] mb-1 [&>path]:fill-gray-900"
                    aria-hidden="true"
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Show Less Button (mobile only) */}
          {!isDesktop && (
            <div>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-16 text-blue-600 hover:no-underline font-semibold py-5 px-8 rounded-[30px] border border-gray-100 font-nunitoSans leading-[25px]"
              >
                {dictionaryLess}
              </button>
            </div>
          )}
        </>
      )}

      {/* Clear All Filters — Always shown */}
      <div>
        <ButtonWrapper ctaVariant="hyperlink" onClick={handleClearAll}>
          {dictionaryClear}
        </ButtonWrapper>
      </div>
    </div>
  );
};

export default AppliedFacetFilters;
